import {Injectable} from '@angular/core';
import {
  ActivatedRouteSnapshot,
  CanActivate,
  CanLoad,
  Route,
  Router,
  RouterStateSnapshot,
  UrlSegment,
  UrlTree
} from '@angular/router';
import {Observable, throwError} from 'rxjs';
import {catchError, map, take} from 'rxjs/operators';
import {AuthService} from './auth.service';
import {ApiService} from './api.service';
import {CommonUtils} from "../utils/common";

/**
 * Strażnik routingu (Guard) zarządzający kontrolą dostępu do modułu konfiguracji
 * pakietów nowych typów wizyt Vetineo.
 * Weryfikuje po stronie serwera API, czy zalogowany użytkownik posiada odpowiednie
 * uprawnienia administracyjne.
 */
@Injectable({
  providedIn: 'root'
})
export class VetineoVisitTypeBundlesAccessGuard implements CanLoad, CanActivate {
  constructor(
    private authService: AuthService,
    private router: Router,
    private apiService: ApiService,
    public commonUtils: CommonUtils
  ) {
  }

  /**
   * Blokuje lub zezwala na asynchroniczne pobranie (Lazy Loading) kodu JavaScript
   * modułu pakietów nowych typów wizyt Vetineo. Zabezpiecza logikę konfiguracyjną przed pobraniem
   * przez nieuprawnioną przeglądarkę, wymuszając weryfikację na endpointcie API.
   *
   * @param route Konfiguracja chronionej ścieżki.
   * @param segments Segmenty adresu URL.
   * @returns Observable emitujący wartość logiczną (true w przypadku pomyślnej autoryzacji serwerowej).
   */
  canLoad(
    route: Route,
    segments: UrlSegment[]
  ): Observable<boolean> | Promise<boolean> | boolean {
    return this.apiService.post('/dashboard/vetineo-visit-type-bundles-access', {}).pipe(take(1),
      catchError(err => {
        if (err.status === 401) {
          this.router.navigate(['signin']).then(() => {
            this.authService.logout();
          });
        }
        return throwError(err);
      })
      , map(response => {
        console.log('check - vetineo-visit-type-bundles - ok');
        return true;
      }));
  }

  /**
   * Weryfikuje uprawnienia przed fizyczną aktywacją i wyrenderowaniem interfejsu
   * zarządzania pakietami nowych typów wizyt Vetineo. Zapewnia integralność sesji poprzez obsługę błędów
   * HTTP 401 (wylogowanie) oraz deleguje komunikację o odmowie dostępu do ustandaryzowanej
   * metody z klasy narzędziowej CommonUtils.
   *
   * @param route Migawka aktywowanej ścieżki.
   * @param state Migawka stanu routera.
   * @returns Observable emitujący wartość logiczną lub bezpieczne drzewo routingu (UrlTree).
   */
  canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean | UrlTree> | Promise<boolean | UrlTree> | boolean | UrlTree {
    return this.apiService.post('/dashboard/vetineo-visit-type-bundles-access', {}).pipe(take(1), catchError(err => {
      if (err.status === 401) {
        this.router.navigate(['signin']).then(() => {
          this.authService.logout();
        });
      }
      return throwError(err);
    }), map(response => {
      console.log('check - canActivate - vetineo-visit-type-bundles - ok', response);

      return this.commonUtils.handleHttpResponseShowInfoToast(response, '', 'Nie masz uprawnień do tej strony')
    }));
  }

}
